// src/components/maps/admin/StepPhotoManager.jsx
import React, { useState, useEffect } from 'react';
import { MapContainer, ImageOverlay, CircleMarker } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import {
  Box,
  Paper,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Grid,
  Alert,
  Snackbar,
  IconButton,
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import indoorMappingService from '../../../services/indoorMappingService';

// Default center: Vinktstraat 1, Deinze, Belgium
const DEFAULT_CENTER = [50.9847, 3.5278];

/**
 * Step Photo Manager component for uploading and managing photos for navigation steps
 */
const StepPhotoManager = ({ floorPlan }) => {
  const [nodes, setNodes] = useState([]);
  const [selectedNode, setSelectedNode] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [direction, setDirection] = useState('forward');
  const [caption, setCaption] = useState('');
  const [uploading, setUploading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  // Load wayfinding nodes
  useEffect(() => {
    if (floorPlan?.id) {
      loadNodes();
    }
  }, [floorPlan]);

  const loadNodes = async () => {
    try {
      const graph = await indoorMappingService.getWayfindingGraph(floorPlan.id);
      setNodes(graph.nodes || []);
    } catch (err) {
      console.error('Failed to load nodes:', err);
      showSnackbar('Failed to load nodes', 'error');
    }
  };

  const showSnackbar = (message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleNodeClick = (node) => {
    setSelectedNode(node);
    setDialogOpen(true);
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setPhotoFile(file);

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUploadPhoto = async () => {
    if (!photoFile || !selectedNode) {
      showSnackbar('Please select a photo', 'warning');
      return;
    }

    try {
      setUploading(true);

      await indoorMappingService.uploadStepPhoto(selectedNode.id, photoFile, {
        direction,
        caption,
      });

      showSnackbar('Photo uploaded successfully', 'success');
      handleCloseDialog();
      loadNodes(); // Reload to get updated node with photo
    } catch (err) {
      showSnackbar(err.message || 'Failed to upload photo', 'error');
    } finally {
      setUploading(false);
    }
  };

  const handleDeletePhoto = async (photoId) => {
    if (!window.confirm('Are you sure you want to delete this photo?')) {
      return;
    }

    try {
      await indoorMappingService.deleteStepPhoto(photoId);
      showSnackbar('Photo deleted successfully', 'success');
      loadNodes();
    } catch (err) {
      showSnackbar(err.message || 'Failed to delete photo', 'error');
    }
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedNode(null);
    setPhotoFile(null);
    setPhotoPreview(null);
    setDirection('forward');
    setCaption('');
  };

  // Handle both snake_case (API) and camelCase field names for image URL
  const imageUrl = floorPlan?.image_url || floorPlan?.imageUrl;
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.caro.care';
  const fullImageUrl = imageUrl
    ? (imageUrl.startsWith('http') ? imageUrl : `${API_BASE_URL}${imageUrl}`)
    : null;

  // Calculate bounds - handle both nested object and flat properties
  const getBounds = () => {
    // Check for nested bounds object
    if (floorPlan?.bounds?.north && floorPlan?.bounds?.south && floorPlan?.bounds?.east && floorPlan?.bounds?.west) {
      return {
        north: floorPlan.bounds.north,
        south: floorPlan.bounds.south,
        east: floorPlan.bounds.east,
        west: floorPlan.bounds.west,
      };
    }
    // Check for flat properties (snake_case from API)
    if (floorPlan?.bounds_north && floorPlan?.bounds_south && floorPlan?.bounds_east && floorPlan?.bounds_west) {
      return {
        north: parseFloat(floorPlan.bounds_north),
        south: parseFloat(floorPlan.bounds_south),
        east: parseFloat(floorPlan.bounds_east),
        west: parseFloat(floorPlan.bounds_west),
      };
    }
    return null;
  };

  const boundsData = getBounds();
  const bounds = boundsData
    ? [
        [boundsData.south, boundsData.west],
        [boundsData.north, boundsData.east],
      ]
    : null;

  const mapCenter = bounds
    ? [(bounds[0][0] + bounds[1][0]) / 2, (bounds[0][1] + bounds[1][1]) / 2]
    : DEFAULT_CENTER;

  return (
    <Box>
      <Paper elevation={2} sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" gutterBottom>
          Step Photo Manager
        </Typography>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Click on a node to upload a photo for that navigation step.
        </Typography>

        <Alert severity="info">
          Photos help users navigate by showing them what they should see at each step.
        </Alert>
      </Paper>

      {/* Map */}
      <Box sx={{ height: 600, border: 1, borderColor: 'divider', borderRadius: 1 }}>
        {bounds && (
          <MapContainer
            center={mapCenter}
            zoom={18}
            style={{ width: '100%', height: '100%' }}
            zoomControl={true}
          >
            {fullImageUrl && (
              <ImageOverlay url={fullImageUrl} bounds={bounds} opacity={1} zIndex={1} />
            )}

            {/* Render nodes */}
            {nodes.map((node) => {
              const hasPhoto = node.photos && node.photos.length > 0;
              // Handle both API format (geo_lat/geo_lng) and local format (lat/lng)
              const nodeLat = node.geo_lat ?? node.y_position ?? node.lat;
              const nodeLng = node.geo_lng ?? node.x_position ?? node.lng;

              if (nodeLat === undefined || nodeLng === undefined) {
                console.warn('[StepPhotoManager] Node missing coordinates:', node);
                return null;
              }

              return (
                <CircleMarker
                  key={node.id}
                  center={[nodeLat, nodeLng]}
                  radius={hasPhoto ? 10 : 8}
                  pathOptions={{
                    color: hasPhoto ? '#4CAF50' : '#2196F3',
                    fillColor: hasPhoto ? '#4CAF50' : '#2196F3',
                    fillOpacity: 0.8,
                    weight: 2,
                  }}
                  eventHandlers={{
                    click: () => handleNodeClick(node),
                  }}
                />
              );
            })}
          </MapContainer>
        )}
      </Box>

      {/* Photo Upload Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          Manage Photos for Node
          <IconButton
            onClick={handleCloseDialog}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {/* Existing Photos */}
          {selectedNode?.photos && selectedNode.photos.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                Existing Photos
              </Typography>
              <Grid container spacing={2}>
                {selectedNode.photos.map((photo) => (
                  <Grid item xs={12} sm={6} key={photo.id}>
                    <Card>
                      <CardMedia component="img" height="200" image={photo.url} alt={photo.caption} />
                      <CardContent>
                        <Typography variant="body2">{photo.caption}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Direction: {photo.direction}
                        </Typography>
                      </CardContent>
                      <CardActions>
                        <Button
                          size="small"
                          color="error"
                          startIcon={<DeleteIcon />}
                          onClick={() => handleDeletePhoto(photo.id)}
                        >
                          Delete
                        </Button>
                      </CardActions>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}

          {/* Upload New Photo */}
          <Typography variant="subtitle2" gutterBottom>
            Upload New Photo
          </Typography>

          <input
            accept="image/*"
            style={{ display: 'none' }}
            id="photo-file-input"
            type="file"
            onChange={handleFileChange}
          />
          <label htmlFor="photo-file-input">
            <Button
              variant="outlined"
              component="span"
              startIcon={<CloudUploadIcon />}
              fullWidth
              sx={{ mb: 2 }}
            >
              Choose Photo
            </Button>
          </label>

          {photoPreview && (
            <Box sx={{ mb: 2 }}>
              <img
                src={photoPreview}
                alt="Preview"
                style={{ width: '100%', maxHeight: 300, objectFit: 'contain' }}
              />
            </Box>
          )}

          <FormControl fullWidth margin="normal">
            <InputLabel>Direction</InputLabel>
            <Select
              value={direction}
              onChange={(e) => setDirection(e.target.value)}
              label="Direction"
            >
              <MenuItem value="forward">Forward</MenuItem>
              <MenuItem value="backward">Backward</MenuItem>
              <MenuItem value="left">Left</MenuItem>
              <MenuItem value="right">Right</MenuItem>
            </Select>
          </FormControl>

          <TextField
            label="Caption"
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            fullWidth
            margin="normal"
            multiline
            rows={2}
            placeholder="E.g., Turn right at the water fountain"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleUploadPhoto}
            variant="contained"
            disabled={!photoFile || uploading}
          >
            {uploading ? 'Uploading...' : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default StepPhotoManager;
